// ============================================================================
// DISBURSEMENT FORM MODAL
// ============================================================================
// Add / Edit form modal for disbursement vouchers.
//
// Follows the prima-web mandatory form-modal blueprint (see CLAUDE.md):
//  - Internal formData state with refs for stable callbacks
//  - Initial snapshot for unsaved-changes (dirty) tracking
//  - Inline field validation via fieldErrors + getFieldError + clearFieldError
//  - 3-stage save flow: validate -> ConfirmDialog -> loading -> toast
//  - Discard-changes ConfirmDialog on close when isDirty
//  - Loading overlay during save (uses var(--modal-loading-overlay))
//  - Minimize-to-bar support (chevronDown header button) -- multi-instance Add
//
// modal-xl -- Header fields + inline editable detail grid.
// Detail rows: Date | Engagement (single async-search field — Client is part of the
//              label, no separate column) | Activity | Amount | Remarks | Remove

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { SyutEngagementSelect, SyutScopedEmployeeSelect } from 'components/ui/SyutEntitySelect';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DisbursementDetailRow {
  id: string; // local id for React key
  voucherDate: string;
  engagementId: string;
  activityId: string;
  /**
   * Saved activity label fields, populated from the API in edit mode.
   * Used to seed the per-row Activity dropdown so a previously-saved
   * activity still renders even if it has since been deactivated or
   * is outside the screen's preloaded chargeTypeKey='D' list.
   * Empty in create mode.
   */
  activityCode?: string;
  activityName?: string;
  amount: string;
  remarks: string;
}

export interface DisbursementFormData {
  voucherNumber: string; // read-only in edit, blank in create (auto-generated)
  employeeId: string;
  voucherDate: string; // header date
  remarks: string;
  details: DisbursementDetailRow[];
  statusCode: 'DRAFT' | 'SUBMITTED' | 'APPROVED';
}

export interface DisbursementFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: DisbursementFormData | null;
  isSaving?: boolean;
  apiError?: string | null;
  onSave: (data: DisbursementFormData) => void;
  onClose: () => void;
  // Lookup options passed from the screen
  employeeOptions: { value: string; label: string }[];
  /**
   * Seed options for the per-row Engagement dropdown. Used purely for label
   * resolution in edit mode — the dropdown itself does server-side search
   * (SyutEngagementSelect → engagementService) so it scales beyond the seed.
   */
  engagementOptions: { value: string; label: string }[];
  activityOptions: { value: string; label: string }[];
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INITIAL_DETAIL_ROW = (): DisbursementDetailRow => ({
  id: `row-${++rowCounter}`,
  voucherDate: '',
  engagementId: '',
  activityId: '',
  amount: '',
  remarks: '',
});

const INITIAL_FORM_DATA: DisbursementFormData = {
  voucherNumber: '',
  employeeId: '',
  voucherDate: '',
  remarks: '',
  details: [],
  statusCode: 'DRAFT',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let rowCounter = 0;

const formatCurrency = (amount: number): string =>
  amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DisbursementFormModal: React.FC<DisbursementFormModalProps> = ({
  isOpen,
  mode,
  initialData,
  isSaving = false,
  apiError,
  onSave,
  onClose,
  employeeOptions,
  engagementOptions,
  activityOptions,
  tabId,
  moduleName,
  onRestoreModal,
  createInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // --- Internal form state ---
  const [formData, setFormData] = useState<DisbursementFormData>({ ...INITIAL_FORM_DATA });
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;

  // Activity dropdown options, augmented with any saved activities that
  // aren't in the screen's preloaded list (e.g. deactivated or
  // reclassified since the disbursement was created). Without this seed
  // the SyutSelect can't resolve the saved activityId to a label and the
  // field appears blank in edit mode.
  const mergedActivityOptions = useMemo(() => {
    const known = new Set(activityOptions.map(o => o.value));
    const seeded = new Set<string>();
    const extras: { value: string; label: string }[] = [];
    for (const row of formData.details) {
      if (!row.activityId || known.has(row.activityId) || seeded.has(row.activityId)) continue;
      const label = [row.activityCode, row.activityName].filter(Boolean).join(' - ').trim();
      extras.push({ value: row.activityId, label: label || row.activityId });
      seeded.add(row.activityId);
    }
    return extras.length === 0 ? activityOptions : [...activityOptions, ...extras];
  }, [activityOptions, formData.details]);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const data: DisbursementFormData = initialData
      ? { ...initialData, details: initialData.details.map((d) => ({ ...d })) }
      : { ...INITIAL_FORM_DATA, details: [INITIAL_DETAIL_ROW()] };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecordCode = formDataRef.current.voucherNumber || undefined;
    const savedFormData: DisbursementFormData = {
      ...formDataRef.current,
      details: formDataRef.current.details.map((d) => ({ ...d })),
    };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(savedFormData) !== savedSnapshot
      : false;

    const idx = currentCreateInstanceId || '1';
    const modalId =
      currentMode === 'create'
        ? `${tabId}-form-modal-create-${idx}`
        : `${tabId}-form-modal-edit-${currentRecordCode}`;

    const title =
      currentMode === 'create'
        ? `Add Disbursement (${idx})`
        : `Edit - ${currentRecordCode}`;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal)
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecordCode : undefined,
          );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    if (isSaving) return;
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, isSaving, onClose]);

  // --- Row manipulation ---
  const addRow = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      details: [...prev.details, INITIAL_DETAIL_ROW()],
    }));
  }, []);

  const removeRow = useCallback((id: string) => {
    setFormData((prev) => {
      if (prev.details.length <= 1) return prev;
      return { ...prev, details: prev.details.filter((r) => r.id !== id) };
    });
  }, []);

  const updateRow = useCallback(
    (id: string, field: keyof Omit<DisbursementDetailRow, 'id'>, value: string) => {
      setFormData((prev) => ({
        ...prev,
        details: prev.details.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
      }));
      // Clear row-level errors
      setFieldErrors((prev) => {
        const key = `${id}-${field}`;
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // --- Total amount ---
  const totalAmount = useMemo(
    () =>
      formData.details.reduce((sum, row) => {
        const val = parseFloat(row.amount);
        return sum + (isNaN(val) ? 0 : val);
      }, 0),
    [formData.details],
  );

  // --- Validation ---
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};

    if (!formData.employeeId) errors.employeeId = 'Employee is required';
    if (!formData.voucherDate) errors.voucherDate = 'Voucher date is required';

    if (formData.details.length === 0) {
      errors.details = 'At least one detail row is required';
    }

    for (const row of formData.details) {
      if (!row.voucherDate) errors[`${row.id}-voucherDate`] = 'Date required';
      if (!row.engagementId) errors[`${row.id}-engagementId`] = 'Engagement required';
      if (!row.activityId) errors[`${row.id}-activityId`] = 'Activity required';
      const amt = parseFloat(row.amount);
      if (!row.amount || isNaN(amt) || amt <= 0) {
        errors[`${row.id}-amount`] = 'Amount must be > 0';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstKey = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // --- Confirmed save ---
  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave(formData);
  }, [onSave, formData]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {/* Loading overlay */}
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'New Disbursement Voucher' : `Edit Disbursement - ${formData.voucherNumber}`}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleClose}
              disabled={isSaving}
              title="Close"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {/* API error banner */}
          {apiError && (
            <div
              style={{
                padding: '0.5rem 0.75rem',
                marginBottom: '0.75rem',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.8125rem',
              }}
            >
              {apiError}
            </div>
          )}

          {/* Section 1: Header */}
          <div className="form-grid-2">
            <div className="form-group" data-field="voucherNumber">
              <label className="form-label">Voucher Number</label>
              <input
                type="text"
                className="form-input"
                value={formData.voucherNumber}
                disabled
                placeholder={mode === 'create' ? 'Auto-generated' : ''}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="form-group" data-field="employeeId">
              <SyutScopedEmployeeSelect
                label={<>Employee <span className="text-danger">*</span></>}
                scopeMode="action"
                moduleId="disbursement-entry"
                value={formData.employeeId || undefined}
                seedOptions={employeeOptions}
                onChange={(val) => {
                  clearFieldError('employeeId');
                  setFormData((prev) => ({ ...prev, employeeId: val || '' }));
                }}
                isClearable
                error={getFieldError('employeeId')}
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="voucherDate">
              <label className="form-label">
                Voucher Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className={`form-input${getFieldError('voucherDate') ? ' form-field-error' : ''}`}
                value={formData.voucherDate}
                disabled={isSaving}
                onChange={(e) => {
                  clearFieldError('voucherDate');
                  setFormData((prev) => ({ ...prev, voucherDate: e.target.value }));
                }}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
              {getFieldError('voucherDate') && (
                <div className="form-error-message">{getFieldError('voucherDate')}</div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <div
                style={{
                  padding: '0.375rem 0.625rem',
                  fontSize: '0.8125rem',
                  color: 'var(--text-secondary)',
                }}
              >
                <span
                  className={`badge badge-${
                    formData.statusCode === 'APPROVED'
                      ? 'active'
                      : formData.statusCode === 'SUBMITTED'
                      ? 'info'
                      : 'inactive'
                  }`}
                >
                  {STATUS_LABELS[formData.statusCode] || formData.statusCode}
                </span>
              </div>
            </div>
          </div>

          {/* Section 2: Disbursement Items */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '0.75rem',
              marginBottom: '0.5rem',
            }}
          >
            <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Disbursement Items
            </h4>
            <button
              className="btn btn-secondary"
              onClick={addRow}
              disabled={isSaving}
              style={{ fontSize: '0.8125rem' }}
            >
              <IconSystem.add size={14} /> Add Row
            </button>
          </div>

          {fieldErrors.details && (
            <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>
              {fieldErrors.details}
            </div>
          )}

          <div
            style={{
              border: '1px solid var(--form-border-color)',
              borderRadius: '4px',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem', minWidth: '900px' }}>
              <thead>
                <tr
                  style={{
                    background: 'var(--grid-header-bg, #dbeafe)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, width: '130px' }}>
                    Date <span className="text-danger">*</span>
                  </th>
                  <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, width: '320px' }}>
                    Engagement <span className="text-danger">*</span>
                  </th>
                  <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, width: '160px' }}>
                    Activity <span className="text-danger">*</span>
                  </th>
                  <th style={{ padding: '0.5rem 0.5rem', textAlign: 'right', fontWeight: 600, width: '110px' }}>
                    Amount <span className="text-danger">*</span>
                  </th>
                  <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600 }}>
                    Remarks
                  </th>
                  <th style={{ padding: '0.5rem 0.5rem', textAlign: 'center', fontWeight: 600, width: '40px' }}>
                    &nbsp;
                  </th>
                </tr>
              </thead>
              <tbody>
                {formData.details.map((row) => {
                  return (
                    <tr key={row.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      {/* Date */}
                      <td style={{ padding: '0.375rem 0.375rem' }}>
                        <input
                          type="date"
                          className={`form-input${fieldErrors[`${row.id}-voucherDate`] ? ' form-field-error' : ''}`}
                          value={row.voucherDate}
                          disabled={isSaving}
                          onChange={(e) => updateRow(row.id, 'voucherDate', e.target.value)}
                          style={{ fontSize: '0.8125rem', padding: '0.25rem 0.375rem', width: '100%' }}
                        />
                        {fieldErrors[`${row.id}-voucherDate`] && (
                          <div className="form-error-message" style={{ fontSize: '0.7rem' }}>
                            {fieldErrors[`${row.id}-voucherDate`]}
                          </div>
                        )}
                      </td>

                      {/* Engagement — single async-search field. Label shows
                          "Client - Engagement Type - Year (CODE)". Searches
                          server-side across client name, type, year, and code,
                          so it scales beyond any preload limit. */}
                      <td style={{ padding: '0.375rem 0.375rem' }} data-field={`${row.id}-engagementId`}>
                        <SyutEngagementSelect
                          value={row.engagementId || undefined}
                          seedOptions={engagementOptions}
                          onChange={(val) => updateRow(row.id, 'engagementId', val || '')}
                          disabled={isSaving}
                          size="sm"
                          placeholder="Search by client, type, or year..."
                          error={fieldErrors[`${row.id}-engagementId`]}
                        />
                      </td>

                      {/* Activity */}
                      <td style={{ padding: '0.375rem 0.375rem' }}>
                        <SyutSelect
                          value={row.activityId || undefined}
                          options={mergedActivityOptions}
                          onChange={(val) => updateRow(row.id, 'activityId', (val as string) || '')}
                          isClearable
                          size="sm"
                          placeholder="Select..."
                          error={fieldErrors[`${row.id}-activityId`]}
                        />
                      </td>

                      {/* Amount */}
                      <td style={{ padding: '0.375rem 0.375rem' }}>
                        <input
                          type="number"
                          className={`form-input${fieldErrors[`${row.id}-amount`] ? ' form-field-error' : ''}`}
                          value={row.amount}
                          disabled={isSaving}
                          min="0"
                          step="0.01"
                          onChange={(e) => updateRow(row.id, 'amount', e.target.value)}
                          style={{
                            fontSize: '0.8125rem',
                            padding: '0.25rem 0.375rem',
                            width: '100%',
                            textAlign: 'right',
                          }}
                        />
                        {fieldErrors[`${row.id}-amount`] && (
                          <div className="form-error-message" style={{ fontSize: '0.7rem' }}>
                            {fieldErrors[`${row.id}-amount`]}
                          </div>
                        )}
                      </td>

                      {/* Remarks */}
                      <td style={{ padding: '0.375rem 0.375rem' }}>
                        <input
                          type="text"
                          className="form-input"
                          value={row.remarks}
                          maxLength={500}
                          placeholder=""
                          disabled={isSaving}
                          onChange={(e) => updateRow(row.id, 'remarks', e.target.value)}
                          style={{ fontSize: '0.8125rem', padding: '0.25rem 0.375rem', width: '100%' }}
                        />
                      </td>

                      {/* Remove */}
                      <td style={{ padding: '0.375rem 0.375rem', textAlign: 'center' }}>
                        <button
                          className="modal-header-btn"
                          onClick={() => removeRow(row.id)}
                          disabled={isSaving || formData.details.length <= 1}
                          title="Remove row"
                          style={{ padding: '0.125rem' }}
                        >
                          <IconSystem.x size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              gap: '0.5rem',
            }}
          >
            <span>Total:</span>
            <span>{formatCurrency(totalAmount)}</span>
          </div>

          {/* Remarks */}
          <div className="form-group" style={{ marginTop: '0.75rem' }} data-field="remarks">
            <label className="form-label">Remarks</label>
            <textarea
              className="form-input"
              value={formData.remarks}
              rows={2}
              maxLength={1000}
              disabled={isSaving}
              placeholder="Optional remarks..."
              onChange={(e) => setFormData((prev) => ({ ...prev, remarks: e.target.value }))}
              style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem', resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`Are you sure you want to ${mode === 'create' ? 'create' : 'update'} this disbursement voucher?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default DisbursementFormModal;
